/**
 * Every joint's axis, drawn on the assembled robot at once.
 *
 * Selecting a joint already draws that one axis. This is the other half of
 * the question a build raises: with the robot standing there, do the two
 * hips turn the same way, is one knee servo in backwards. That only reads
 * off the whole robot at once, so the mode puts a coloured axis and a
 * right-hand-rule curl on every movable joint, and rings the ones whose
 * left/right partner disagrees with them (see `jointAxisAudit.ts` for what
 * "disagrees" means).
 *
 * Colour is the axis' world direction, so a leg whose servos line up reads
 * as a column of one colour; the arrowhead carries the sign, because two
 * axes along the same line turning opposite ways is exactly the mistake
 * this is here to catch.
 *
 * Two things keep it readable on a robot with tens of joints, both driven
 * from one per-frame pass over the gizmos:
 *
 * - every gizmo is sized in pixels rather than metres, so it neither
 *   vanishes into the mesh when the whole robot is in view nor swamps the
 *   screen when one joint is;
 * - names are written only for the joints worth naming (the flagged ones,
 *   the one being pointed at), and any label that would land on top of a
 *   better one is hidden rather than drawn over it.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Html, Line } from "@react-three/drei";
import * as THREE from "three";

import {
  useInstances,
  useJointAxes,
  useJointAxesHoveredName,
  useJointAxesLabelMode,
  useJointAxesMirrorAxis,
  useJointAxesMode,
  useJointAxesShowFrames,
  useJointAxesSizePx,
  useModuleAxesModuleId,
  useModuleAxesPending,
  useViewerStore,
} from "../store";
import { useWebSocket } from "../WebSocket";
import type { JointAxesLabelMode } from "../store/types";
import type { JointAxisData } from "../types";
import { auditJointAxes, axisDirection, type Vec3 } from "./jointAxisAudit";
import { compactJointLabel, LABEL_PRIORITY, type LabelBox, pickVisibleLabels } from "./jointLabels";
import { screenSpaceScale } from "./screenSpace";

/** Axis colour by the world direction it lies along; magenta for none of them. */
const DIRECTION_COLOURS: Record<string, string> = {
  "+X": "#ff6b6b",
  "-X": "#ff6b6b",
  "+Y": "#51cf66",
  "-Y": "#51cf66",
  "+Z": "#4dabf7",
  "-Z": "#4dabf7",
  oblique: "#e599f7",
};

/** Ring and label colour for a joint whose partner disagrees with it. */
const WARNING_COLOUR = "#ff922b";
/** Ring colour for the joint the pointer is on in the panel's list. */
const HOVER_COLOUR = "#ffffff";
/** Colour for an axis drawn as the module editor is about to write it. */
const PREVIEW_COLOUR = "#ff922b";

/** Shared so a joint that is not being dragged keeps a stable prop. */
const NO_DRAG_OFFSET: Vec3 = [0, 0, 0];

const FRAME_COLOURS = ["#ff6b6b", "#51cf66", "#4dabf7"];

/**
 * The length the gizmo is modelled at before the frame loop scales it to
 * the size the user asked for; every ratio below is a fraction of it.
 *
 * Five centimetres rather than a metre so that the one frame between a
 * gizmo mounting and the loop first sizing it shows something the size of
 * a module rather than the size of the room.
 */
const UNIT = 0.05;
const CURL_RADIUS_RATIO = 0.36;
const CURL_ARC = Math.PI * 1.4;
const CURL_START = -Math.PI * 0.2;
const HEAD_LENGTH_RATIO = 0.28;
const HEAD_RADIUS_RATIO = 0.11;
const LABEL_DISTANCE_RATIO = 1.3;
/** How far off the viewport a label may sit and still count as on screen. */
const OFFSCREEN_MARGIN_PX = 40;

/** What the per-frame pass needs to size a gizmo and place its label. */
interface GizmoHandle {
  group: React.RefObject<THREE.Group | null>;
  labelElement: React.RefObject<HTMLDivElement | null>;
  /** Label anchor in the gizmo's own (unit-length) frame. */
  labelAnchor: THREE.Vector3;
  priority: number;
}

type GizmoRegistry = Map<string, GizmoHandle>;

/** Rotation taking +Z onto a direction, for anything drawn in a plane. */
function quaternionFromZTo(direction: THREE.Vector3): THREE.Quaternion {
  return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), direction.clone().normalize());
}

/**
 * One joint: a double-ended axis, an arrowhead on the positive end and a
 * curl turning the way a positive command turns it.
 */
function JointAxisGizmo({
  joint,
  flagged,
  hovered,
  preview,
  showFrame,
  showLabel,
  dragOffset,
  register,
  onSelect,
}: {
  joint: JointAxisData;
  flagged: boolean;
  hovered: boolean;
  /** The module editor has marked this joint, and has not written it yet. */
  preview: boolean;
  showFrame: boolean;
  showLabel: boolean;
  /** Where this joint's module has been dragged to since the server last spoke. */
  dragOffset: Vec3;
  register: (name: string, handle: GizmoHandle | null) => void;
  onSelect: (name: string) => void;
}) {
  // A marked joint is drawn the way it will read once written, so the
  // decision is made against the arrow rather than against the word
  // "Reverse" -- it is the only thing on screen that answers "which way
  // will it turn then".
  const drawnAxis: Vec3 = preview ? [-joint.axis[0], -joint.axis[1], -joint.axis[2]] : joint.axis;
  const direction = axisDirection(drawnAxis);
  const colour = preview ? PREVIEW_COLOUR : (DIRECTION_COLOURS[direction] ?? DIRECTION_COLOURS.oblique);
  const isPrismatic = joint.joint_type === "prismatic";
  const groupRef = useRef<THREE.Group | null>(null);
  const labelElement = useRef<HTMLDivElement | null>(null);

  const geometry = useMemo(() => {
    const axis = new THREE.Vector3(...drawnAxis).normalize();
    const headLength = UNIT * HEAD_LENGTH_RATIO;
    const headRadius = UNIT * HEAD_RADIUS_RATIO;
    const axisQuaternion = quaternionFromZTo(axis);

    // Cones point +Y in three.js, so the head is turned onto the axis
    // separately from everything drawn in the perpendicular plane.
    const headQuaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis);

    const shaft: [number, number, number][] = [
      axis.clone().multiplyScalar(-UNIT).toArray(),
      axis.clone().multiplyScalar(UNIT).toArray(),
    ];

    const curlRadius = UNIT * CURL_RADIUS_RATIO;
    const segments = 28;
    const curl: [number, number, number][] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = CURL_START + (CURL_ARC * i) / segments;
      curl.push([Math.cos(angle) * curlRadius, Math.sin(angle) * curlRadius, 0]);
    }
    const endAngle = CURL_START + CURL_ARC;
    // Positive rotation runs anticlockwise seen from the tip of the axis,
    // so the curl head points along the tangent there.
    const tangent = new THREE.Vector3(-Math.sin(endAngle), Math.cos(endAngle), 0);

    return {
      axis,
      axisQuaternion,
      headQuaternion,
      tailHeadQuaternion: new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        axis.clone().negate()
      ),
      headLength,
      headRadius,
      shaft,
      headPosition: axis.clone().multiplyScalar(UNIT - headLength / 2),
      tailHeadPosition: axis.clone().multiplyScalar(-(UNIT - headLength / 2)),
      curl,
      curlHeadQuaternion: new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), tangent),
      curlHeadPosition: new THREE.Vector3(
        Math.cos(endAngle) * curlRadius,
        Math.sin(endAngle) * curlRadius,
        0
      ),
      labelAnchor: axis.clone().multiplyScalar(UNIT * LABEL_DISTANCE_RATIO),
    };
  }, [joint.axis, preview]);

  const frameQuaternion = useMemo(() => new THREE.Quaternion(...joint.quaternion), [joint.quaternion]);

  const priority = flagged
    ? LABEL_PRIORITY.flagged
    : hovered
      ? LABEL_PRIORITY.hovered
      : LABEL_PRIORITY.ordinary;

  useEffect(() => {
    register(joint.name, {
      group: groupRef,
      labelElement,
      labelAnchor: geometry.labelAnchor,
      priority,
    });
    return () => register(joint.name, null);
  }, [register, joint.name, geometry.labelAnchor, priority]);

  // Floating next to the axis there is room for a short name, not for a
  // generated instance id and a URDF joint name; the full name is a hover
  // away, and renaming the joint replaces this with whatever the user
  // called it. See jointLabels.ts.
  const label = compactJointLabel(joint.name, joint.display_name);

  return (
    <group
      ref={groupRef}
      position={[
        joint.position[0] + dragOffset[0],
        joint.position[1] + dragOffset[1],
        joint.position[2] + dragOffset[2],
      ]}
      raycast={() => null}
    >
      <Line points={geometry.shaft} color={colour} lineWidth={2.5} />

      {/* The head is on the positive end: the way a positive command turns it. */}
      <mesh position={geometry.headPosition} quaternion={geometry.headQuaternion}>
        <coneGeometry args={[geometry.headRadius, geometry.headLength, 12]} />
        <meshStandardMaterial color={colour} emissive={colour} emissiveIntensity={0.25} />
      </mesh>

      {isPrismatic && (
        // A slider runs both ways, so it gets a head at each end instead of a curl.
        <mesh position={geometry.tailHeadPosition} quaternion={geometry.tailHeadQuaternion}>
          <coneGeometry args={[geometry.headRadius, geometry.headLength, 12]} />
          <meshStandardMaterial color={colour} emissive={colour} emissiveIntensity={0.25} />
        </mesh>
      )}

      {!isPrismatic && (
        <group quaternion={geometry.axisQuaternion}>
          <Line points={geometry.curl} color={colour} lineWidth={2} />
          <mesh position={geometry.curlHeadPosition} quaternion={geometry.curlHeadQuaternion}>
            <coneGeometry args={[geometry.headRadius * 0.7, geometry.headLength * 0.7, 10]} />
            <meshStandardMaterial color={colour} emissive={colour} emissiveIntensity={0.25} />
          </mesh>
        </group>
      )}

      {(flagged || hovered || preview) && (
        <mesh quaternion={geometry.axisQuaternion}>
          <torusGeometry args={[UNIT * (hovered ? 0.72 : 0.62), UNIT * 0.05, 8, 40]} />
          <meshBasicMaterial color={preview ? PREVIEW_COLOUR : flagged ? WARNING_COLOUR : HOVER_COLOUR} />
        </mesh>
      )}

      {showFrame && (
        <group quaternion={frameQuaternion}>
          {FRAME_COLOURS.map((frameColour, index) => {
            const end: [number, number, number] = [0, 0, 0];
            end[index] = UNIT * 0.7;
            return (
              <Line
                key={frameColour}
                points={[[0, 0, 0], end]}
                color={frameColour}
                lineWidth={1.2}
                transparent
                opacity={0.85}
              />
            );
          })}
        </group>
      )}

      <mesh>
        <sphereGeometry args={[UNIT * 0.09, 12, 12]} />
        <meshStandardMaterial color={flagged && !preview ? WARNING_COLOUR : colour} />
      </mesh>

      {showLabel && (
        <Html position={geometry.labelAnchor} center>
          <div
            ref={labelElement}
            title={joint.name}
            onPointerDown={(event) => {
              event.stopPropagation();
              onSelect(joint.name);
            }}
            style={{
              background: "rgba(20, 20, 20, 0.82)",
              border: `1px solid ${preview ? PREVIEW_COLOUR : flagged ? WARNING_COLOUR : hovered ? HOVER_COLOUR : colour}`,
              borderRadius: 4,
              color: "#fff",
              cursor: "pointer",
              font: "600 11px/1.1 ui-monospace, SFMono-Regular, Menlo, monospace",
              padding: "2px 6px",
              whiteSpace: "nowrap",
              userSelect: "none",
            }}
          >
            {preview ? "⇄ " : flagged ? "⚠ " : ""}
            {label}
            <span style={{ color: colour, marginLeft: 6 }}>{direction}</span>
          </div>
        </Html>
      )}
    </group>
  );
}

/**
 * Size every gizmo to the pixels asked for, and hide the labels that would
 * be drawn on top of better ones.
 *
 * Both jobs need the camera each frame and both walk the same gizmos, so
 * they share one pass rather than each taking a subscription per joint.
 */
function GizmoFrameLoop({ registry, sizePx }: { registry: React.RefObject<GizmoRegistry>; sizePx: number }) {
  const worldPosition = useRef(new THREE.Vector3());
  const anchor = useRef(new THREE.Vector3());

  useFrame(({ camera, size }) => {
    const gizmos = registry.current;
    if (!gizmos || gizmos.size === 0) {
      return;
    }
    const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov : 50;
    const boxes: LabelBox[] = [];

    for (const [name, handle] of gizmos) {
      const group = handle.group.current;
      if (!group) {
        continue;
      }
      group.getWorldPosition(worldPosition.current);
      const distance = camera.position.distanceTo(worldPosition.current);
      // The model is UNIT long, so the factor that gives it `sizePx` pixels
      // is the world length divided by that.
      const scale = screenSpaceScale(distance, fov, size.height, sizePx) / UNIT;
      group.scale.setScalar(scale);

      const element = handle.labelElement.current;
      if (!element) {
        continue;
      }
      anchor.current.copy(handle.labelAnchor).multiplyScalar(scale).add(worldPosition.current);
      anchor.current.project(camera);
      const x = (anchor.current.x * 0.5 + 0.5) * size.width;
      const y = (-anchor.current.y * 0.5 + 0.5) * size.height;
      const width = element.offsetWidth;
      const height = element.offsetHeight;
      boxes.push({
        name,
        priority: handle.priority,
        distance,
        left: x - width / 2,
        top: y - height / 2,
        width,
        height,
        onScreen:
          anchor.current.z < 1
          && x > -OFFSCREEN_MARGIN_PX
          && x < size.width + OFFSCREEN_MARGIN_PX
          && y > -OFFSCREEN_MARGIN_PX
          && y < size.height + OFFSCREEN_MARGIN_PX,
      });
    }

    if (boxes.length === 0) {
      return;
    }
    const visible = pickVisibleLabels(boxes);
    for (const box of boxes) {
      const element = gizmos.get(box.name)?.labelElement.current;
      if (element) {
        element.style.visibility = visible.has(box.name) ? "visible" : "hidden";
      }
    }
  });

  return null;
}

/** Which joints get their name written, given the mode the user chose. */
function wantsLabel(
  mode: JointAxesLabelMode,
  { flagged, hovered }: { flagged: boolean; hovered: boolean }
): boolean {
  if (mode === "off") {
    return false;
  }
  if (mode === "all") {
    return true;
  }
  return flagged || hovered;
}

export function JointAxesOverlay() {
  const mode = useJointAxesMode();
  const axes = useJointAxes();
  const labelMode = useJointAxesLabelMode();
  const showFrames = useJointAxesShowFrames();
  const sizePx = useJointAxesSizePx();
  const hoveredName = useJointAxesHoveredName();
  const mirrorAxis = useJointAxesMirrorAxis();
  const { sendHighlightJoint } = useWebSocket();
  // A drag moves the meshes on the client and only tells the server as it
  // goes (see SceneMesh, which offsets the same way). Without this the axes
  // would sit at the module's old place for the length of the gesture.
  const dragOffset = useViewerStore((state) => state.dragOffset);
  const draggingInstanceIds = useViewerStore((state) => state.draggingInstanceIds);
  // What the module axis editor is about to write. It changes the module,
  // so every instance of it previews, not only the one being edited.
  const instances = useInstances();
  const editedModuleId = useModuleAxesModuleId();
  const markedJointNames = useModuleAxesPending();

  const registry = useRef<GizmoRegistry>(new Map());
  const register = useCallback((name: string, handle: GizmoHandle | null) => {
    if (handle) {
      registry.current.set(name, handle);
    } else {
      registry.current.delete(name);
    }
  }, []);

  const flaggedNames = useMemo(() => {
    if (!mode || axes.length === 0) {
      return new Set<string>();
    }
    const mirrorNormal: Vec3 = mirrorAxis === "x" ? [1, 0, 0] : [0, 1, 0];
    const audit = auditJointAxes(axes, { mirrorNormal });
    const names = new Set<string>();
    for (const pair of audit.skewed) {
      names.add(pair.left.name);
      names.add(pair.right.name);
    }
    return names;
  }, [mode, axes, mirrorAxis]);

  const previewInstanceIds = new Set(
    editedModuleId === null
      ? []
      : instances.filter((instance) => instance.module_name === editedModuleId).map((i) => i.instance_id)
  );
  const isPreviewed = (joint: JointAxisData): boolean => {
    if (editedModuleId === null || joint.instance_id === null) {
      return false;
    }
    if (!previewInstanceIds.has(joint.instance_id)) {
      return false;
    }
    // The editor names the module's own joints; the overlay names the
    // placed ones as `prefix/joint`.
    return markedJointNames[joint.name.split("/").slice(1).join("/")] === true;
  };

  if (!mode || axes.length === 0) {
    return null;
  }

  return (
    <group>
      <GizmoFrameLoop registry={registry} sizePx={sizePx} />
      {axes.map((joint) => {
        const flagged = flaggedNames.has(joint.name);
        const hovered = hoveredName === joint.name;
        const preview = isPreviewed(joint);
        return (
          <JointAxisGizmo
            key={joint.name}
            joint={joint}
            flagged={flagged}
            hovered={hovered}
            preview={preview}
            showFrame={showFrames}
            showLabel={wantsLabel(labelMode, { flagged, hovered: hovered || preview })}
            dragOffset={
              joint.instance_id && draggingInstanceIds[joint.instance_id] ? dragOffset : NO_DRAG_OFFSET
            }
            register={register}
            onSelect={sendHighlightJoint}
          />
        );
      })}
    </group>
  );
}
